// ID-178: Reddit Ads identifiers, shared by the browser (useTracking) and the server-only sGTM emitters.

/** Landing-page query param Reddit appends to ad clicks. */
export const REDDIT_CLICK_ID_PARAM = 'rdt_cid';
/** First-party cookie the click id is persisted in (same `cid_` prefix as the other click ids). */
export const REDDIT_CLICK_ID_COOKIE = `cid_${REDDIT_CLICK_ID_PARAM}`;
export const REDDIT_CLICK_ID_COOKIE_DAYS = 90;
/** The Reddit pixel's own first-party cookie. */
export const REDDIT_UUID_COOKIE = '_rdt_uuid';

const MAX_ID_LENGTH = 500;

export type RedditIds = {
  rdtCid: string | null;
  rdtUuid: string | null;
};

/** sGTM event-data keys: top level AND under `identifiers`, so a GTM Event Data variable can read either. */
export type RedditEventData = {
  rdt_cid?: string;
  rdt_uuid?: string;
  identifiers?: { rdt_cid?: string; rdt_uuid?: string };
};

const clean = (value: unknown): string | null => {
  const trimmed = typeof value === 'string' ? value.trim() : '';
  return trimmed ? trimmed.slice(0, MAX_ID_LENGTH) : null;
};

/** Reads both ids through any cookie getter (document.cookie, h3 getCookie, parseCookies map). */
export const readRedditIds = (getCookie: (name: string) => string | null | undefined): RedditIds => ({
  rdtCid: clean(getCookie(REDDIT_CLICK_ID_COOKIE)),
  rdtUuid: clean(getCookie(REDDIT_UUID_COOKIE)),
});

/** Reads ids stored on the user (checkoutTracking / trackingInfo Json), tolerant of missing or bad data. */
export const readStoredRedditIds = (raw: unknown): RedditIds => {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return { rdtCid: null, rdtUuid: null };
  const obj = raw as Record<string, unknown>;
  return { rdtCid: clean(obj.rdtCid), rdtUuid: clean(obj.rdtUuid) };
};

/** Event params for a server-only emitter; empty when there is nothing to send. */
export const buildRedditEventData = ({ rdtCid, rdtUuid }: Partial<RedditIds>): RedditEventData => {
  const keys = { ...(rdtCid ? { rdt_cid: rdtCid } : {}), ...(rdtUuid ? { rdt_uuid: rdtUuid } : {}) };
  return Object.keys(keys).length ? { ...keys, identifiers: { ...keys } } : {};
};
